import React, { useState, useEffect } from 'react';
import { MoreVertical } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { getSafeImageSrc } from '../../utils/imageUtils';
import AssistantOptionsMenu from "../../components/AssistantOptionsMenu";
import { CoOwnerAvatars } from '../../components/CoOwnerAvatar';

// Utility function for relative time
const getRelativeTime = (timestamp: string): { relative: string; full: string } => {
  const now = new Date();
  const date = new Date(timestamp);
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  const fullDate = date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  if (diffInSeconds < 60) {
    return { relative: 'Just now', full: fullDate };
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return { relative: `${minutes} minute${minutes > 1 ? 's' : ''} ago`, full: fullDate };
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return { relative: `${hours} hour${hours > 1 ? 's' : ''} ago`, full: fullDate };
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return { relative: `${days} day${days > 1 ? 's' : ''} ago`, full: fullDate };
  } else if (diffInSeconds < 2629746) {
    const weeks = Math.floor(diffInSeconds / 604800);
    return { relative: `${weeks} week${weeks > 1 ? 's' : ''} ago`, full: fullDate };
  } else if (diffInSeconds < 31556952) {
    const months = Math.floor(diffInSeconds / 2629746);
    return { relative: `${months} month${months > 1 ? 's' : ''} ago`, full: fullDate };
  } else {
    const years = Math.floor(diffInSeconds / 31556952);
    return { relative: `${years} year${years > 1 ? 's' : ''} ago`, full: fullDate };
  }
};

const SharedAssistantListView = ({
  assistants,
  handleAssistantView,
  showOptions,
  setShowOptions,
  optionsRef,
  handleCoOwner,
  handleCopyAppUrl,
  handleClone,
  handleExport,
  handleMergeClick,
  defaultAIFace
}: any) => {
  const [imageSrcs, setImageSrcs] = useState<{ [key: string]: string }>({});
  const [showTooltip, setShowTooltip] = useState<{ [key: string]: boolean }>({});
  const [tooltipPosition, setTooltipPosition] = useState<{ [key: string]: { x: number; y: number } }>({});
  const [menuPosition, setMenuPosition] = useState<{ [key: string]: { x: number; y: number } }>({});

  useEffect(() => {
    const fetchImageSrc = async (assistant: any) => {
      let safeAiIconSrc = assistant.app_ai_face || defaultAIFace;
      const safeSrc = await getSafeImageSrc(safeAiIconSrc);
      setImageSrcs((prev: { [key: string]: string }) => ({
        ...prev,
        [assistant.app_id]: safeSrc,
      }));
    };

    // Fetch images for all assistants
    assistants.forEach((assistant: any) => fetchImageSrc(assistant));

  }, [assistants, defaultAIFace]);

  // Simple scroll handler only
  useEffect(() => {
    const handleScroll = () => {
      setShowOptions({});
    };

    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <div className="space-y-3">
      {assistants.length > 0 ? (
        assistants.map((assistant: any) => {
          const timeData = getRelativeTime(assistant.last_modified);
          
          return (
            <div
              key={assistant.app_id}
              className="bg-white border border-gray-200 rounded-xl hover:shadow-md hover:border-gray-300 transition-all duration-200 cursor-pointer relative group"
              role="button"
              tabIndex={0}
              onClick={(e: any) => {
                if (!(e.target as Element).closest('.options-button, .options-menu')) {
                  handleAssistantView(assistant);
                }
              }}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleAssistantView(assistant);
                }
              }}
            >
              <div className="p-5">
                {/* Desktop Layout */}
                <div className="hidden lg:grid lg:grid-cols-12 lg:items-center lg:gap-6">
                  {/* Assistant Info - Icon + Name */}
                  <div className="col-span-4 flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0 ring-1 ring-gray-200">
                      <img
                        src={imageSrcs[assistant.app_id] || defaultAIFace}
                        alt="AI Face"
                        className="h-7 w-7 object-cover rounded-md"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-gray-900 text-base leading-6 truncate">
                        {assistant.app_name.length > 40
                          ? `${assistant.app_name.slice(0, 40)}...`
                          : assistant.app_name}
                      </div>
                      <div className="text-sm text-gray-500 mt-1 truncate">
                        Owner: {assistant.owner_name}
                      </div>
                    </div>
                  </div>
                  
                  {/* Last Modified */}
                  <div className="col-span-3">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                      Last Modified
                    </div>
                    <div
                      className="text-sm text-gray-700 cursor-help font-medium"
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTooltipPosition(prev => ({
                          ...prev,
                          [assistant.app_id]: {
                            x: rect.left + 20,
                            y: rect.top - 10
                          }
                        }));
                        setShowTooltip(prev => ({ ...prev, [assistant.app_id]: true }));
                      }}
                      onMouseLeave={() => {
                        setShowTooltip(prev => ({ ...prev, [assistant.app_id]: false }));
                      }}
                    >
                      {timeData.relative}
                    </div>
                  </div>
                  
                  {/* Shared with */}
                  <div className="col-span-3">
                    {Array.isArray(assistant.co_owner) && assistant.co_owner.length > 0 ? (
                      <>
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                          Shared with
                        </div>
                        <div className="relative z-10">
                          <CoOwnerAvatars 
                            coOwners={assistant.co_owner} 
                            maxDisplay={3} 
                            onClick={() => handleCoOwner(assistant)}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-gray-400 italic">
                        Not shared
                      </div>
                    )}
                  </div>
                  
                  {/* Status & Options */}
                  <div className="col-span-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {assistant.app_publishstatus.toLowerCase() === 'published' ? (
                        <>
                          <FontAwesomeIcon 
                            icon={faCircleCheck} 
                            className="h-4 w-4 text-gray-700 flex-shrink-0" 
                          />
                          <span className="text-sm font-medium text-gray-800">
                            Published
                          </span>
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon 
                            icon={faEyeSlash} 
                            className="h-4 w-4 text-gray-500 flex-shrink-0" 
                          />
                          <span className="text-sm font-medium text-gray-600">
                            Unpublished
                          </span>
                        </>
                      )}
                    </div>
                    
                    {/* Options Menu */}
                    <div className="relative flex-shrink-0">
                      <button
                        onClick={(e: any) => {
                          e.stopPropagation();

                          const buttonElement = e.currentTarget;
                          const rect = buttonElement.getBoundingClientRect();
                          const menuHeight = 280; // Approximate menu height
                          const menuWidth = 192; // w-48 = 192px
                          const viewportHeight = window.innerHeight;

                          // Calculate optimal position
                          let x = rect.right - menuWidth;
                          let y = rect.bottom + 4;

                          // Adjust if menu would go off the right edge
                          if (x < 0) {
                            x = rect.left;
                          }

                          // Adjust if menu would go off the bottom edge
                          if (y + menuHeight > viewportHeight) {
                            y = rect.top - menuHeight + 110; // Reduced gap when showing above
                          }

                          // Ensure menu doesn't go above viewport
                          if (y < 0) {
                            y = 110;
                          }

                          // Set position
                          setMenuPosition(prev => ({
                            ...prev,
                            [assistant.app_id]: { x, y }
                          }));

                          // Toggle menu (close all others)
                          setShowOptions({ [assistant.app_id]: true });
                        }}
                        className="text-gray-400 hover:text-gray-700 options-button p-2 rounded-full hover:bg-gray-100 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-gray-300"
                        aria-label="Open options menu"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>

                    </div>
                  </div>
                </div>

                {/* Mobile/Tablet Layout */}
                <div className="lg:hidden space-y-4">
                  {/* Header Row */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0 ring-1 ring-gray-200">
                        <img
                          src={imageSrcs[assistant.app_id] || defaultAIFace}
                          alt="AI Face"
                          className="h-7 w-7 object-cover rounded-md"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-gray-900 text-base leading-6 truncate">
                          {assistant.app_name}
                        </div>
                        <div className="text-sm text-gray-500 mt-1 truncate">
                          Owner: {assistant.owner_name}
                        </div>
                      </div>
                    </div>
                    
                    {/* Options Menu */}
                    <div className="relative flex-shrink-0">
                      <button
                        onClick={(e: any) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const menuHeight = 280; // Approximate menu height
                          const menuWidth = 192; // w-48 = 192px
                          const viewportHeight = window.innerHeight;
                          const viewportWidth = window.innerWidth;

                          // Calculate optimal position
                          let x = rect.right - menuWidth;
                          let y = rect.bottom + 4;

                          // Adjust if menu would go off the right edge
                          if (x < 0) {
                            x = rect.left;
                          }

                          // Adjust if menu would go off the bottom edge
                          if (y + menuHeight > viewportHeight) {
                            y = rect.top - menuHeight + 4; // Reduced gap when showing above
                          }

                          // Ensure menu doesn't go above viewport
                          if (y < 0) {
                            y = 4;
                          }

                          setMenuPosition(prev => ({
                            ...prev,
                            [assistant.app_id]: { x, y }
                          }));
                          setShowOptions((prev: { [key: string]: boolean }) => {
                            const isCurrentlyOpen = prev[assistant.app_id];
                            // If menu is currently open, close it; if closed, open it
                            if (isCurrentlyOpen) {
                              return { ...prev, [assistant.app_id]: false };
                            } else {
                              // Close all other menus and open this one
                              return { [assistant.app_id]: true };
                            }
                          });
                        }}
                        className="text-gray-400 hover:text-gray-700 options-button p-2 rounded-full hover:bg-gray-100 transition-all duration-150"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>

                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-100">
                    {/* Last Modified */}
                    <div>
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                        Last Modified
                      </div>
                      <div className="text-sm text-gray-700 font-medium">
                        {timeData.relative}
                      </div>
                    </div>
                    
                    {/* Status */}
                    <div className="text-right">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                        Status
                      </div>
                      <div className="flex items-center justify-end gap-2">
                        {assistant.app_publishstatus.toLowerCase() === 'published' ? (
                          <>
                            <span className="text-sm font-medium text-gray-800">
                              Published
                            </span>
                            <FontAwesomeIcon 
                              icon={faCircleCheck} 
                              className="h-4 w-4 text-gray-700" 
                            />
                          </>
                        ) : (
                          <>
                            <span className="text-sm font-medium text-gray-600">
                              Unpublished
                            </span>
                            <FontAwesomeIcon 
                              icon={faEyeSlash} 
                              className="h-4 w-4 text-gray-500" 
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Shared with - Full width if exists */}
                  {Array.isArray(assistant.co_owner) && assistant.co_owner.length > 0 && (
                    <div className="pt-3 border-t border-gray-100">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                        Shared with
                      </div>
                      <CoOwnerAvatars 
                        coOwners={assistant.co_owner} 
                        maxDisplay={5} 
                        onClick={() => handleCoOwner(assistant)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="col-span-full p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="text-gray-400 mb-3">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <p className="text-gray-500 text-base leading-relaxed">
              No shared chatbots found.
            </p>
          </div>
        </div>
      )}
    </div>

      {/* Portal-style Tooltips */}
      {Object.keys(showTooltip).map((assistantId) => {
        if (!showTooltip[assistantId] || !tooltipPosition[assistantId]) return null;

        const assistant = assistants.find((a: any) => a.app_id === assistantId);
        if (!assistant) return null;

        const timeData = getRelativeTime(assistant.last_modified);
        const pos = tooltipPosition[assistantId];

        return (
          <div
            key={`tooltip-${assistantId}`}
            className="fixed z-[10000] px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-xl pointer-events-none border border-gray-700 whitespace-nowrap"
            style={{
              left: pos.x,
              top: pos.y,
              transform: 'translate(0, -100%)',
            }}
          >
            {timeData.full}
            <div
              className="absolute top-full left-6 w-2 h-2 bg-gray-900 rotate-45 -mt-1 border-r border-b border-gray-700"
            ></div>
          </div>
        );
      })}

      {/* Portal-style Options Menus */}
      {Object.keys(showOptions).map((assistantId) => {
        if (!showOptions[assistantId] || !menuPosition[assistantId]) return null;

        const assistant = assistants.find((a: any) => a.app_id === assistantId);
        if (!assistant) return null;

        const pos = menuPosition[assistantId];

        return (
          <div
            key={`menu-${assistantId}`}
            className="fixed z-[10000] w-48 rounded-lg shadow-xl bg-white ring-1 ring-black ring-opacity-10 border border-gray-100 options-menu"
            style={{
              left: pos.x,
              top: pos.y,
            }}
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="options-menu"
          >
            <div className="py-1">
              <AssistantOptionsMenu
                assistant={assistant}
                optionsRef={optionsRef}
                handleCoOwner={handleCoOwner}
                handleCopyAppUrl={(assistantId: string) => {
                  handleCopyAppUrl(assistantId);
                  setShowOptions((prev: { [key: string]: boolean }) => ({ ...prev, [assistantId]: false }));
                }}
                handleClone={(assistantId: string, assistantName: string) => {
                  handleClone(assistantId, assistantName);
                  setShowOptions((prev: { [key: string]: boolean }) => ({ ...prev, [assistantId]: false }));
                }}
                handleExport={(assistant: any) => {
                  handleExport(assistant);
                  setShowOptions((prev: { [key: string]: boolean }) => ({ ...prev, [assistant.app_id]: false }));
                }}
                handleMergeClick={handleMergeClick}
                type="shared"
              />
            </div>
          </div>
        );
      })}
    </>
  );
};

export default SharedAssistantListView;